import React, { createContext, useContext, useEffect, useState } from 'react';
import { signIn as awsSignIn, signOut as awsSignOut, signUp as awsSignUp, confirmSignUp as awsConfirmSignUp, getCurrentUser, fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import type { HubCallback } from '@aws-amplify/core';

function deriveDisplayName(email: string): string {
  const localPart = email.split('@')[0];
  return localPart
    .split(/[._-]/)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

const GROUP_ROLE_MAP: Record<string, string> = {
  'Sales-toolkit-admin': 'Admin',
  'Sales-toolkit-manager': 'Manager',
  'Sales-toolkit-viewer': 'Viewer',
};

interface AuthUser {
  username: string;
  userId: string;
  displayName: string;
  email: string;
  role: string;
  signInDetails?: {
    loginId?: string;
  };
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  signIn: (username: string, password: string) => Promise<any>;
  signOut: () => Promise<any>;
  signUp: (username: string, password: string, email: string) => Promise<any>;
  confirmSignUp: (username: string, code: string) => Promise<any>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkUser();
    setupAuthListener();
  }, []);

  const buildUser = async (): Promise<AuthUser | null> => {
    try {
      const currentUser = await getCurrentUser();
      const [attributes, session] = await Promise.all([
        fetchUserAttributes(),
        fetchAuthSession(),
      ]);
      const email = attributes.email || currentUser.signInDetails?.loginId || '';
      const displayName = attributes.name ||
        (attributes.given_name && attributes.family_name
          ? `${attributes.given_name} ${attributes.family_name}`
          : attributes.given_name || (email ? deriveDisplayName(email) : currentUser.username));
      const groups: string[] = (session.tokens?.accessToken?.payload?.['cognito:groups'] as string[]) || [];
      const matchedGroup = groups.find(g => GROUP_ROLE_MAP[g]);
      const role = matchedGroup ? GROUP_ROLE_MAP[matchedGroup] : '';
      return {
        username: currentUser.username,
        userId: currentUser.userId,
        displayName,
        email,
        role,
        signInDetails: currentUser.signInDetails,
      };
    } catch {
      return null;
    }
  };

  const checkUser = async () => {
    const userData = await buildUser();
    setUser(userData);
    setLoading(false);
  };

  const setupAuthListener = () => {
    const authListener: HubCallback = ({ payload: { event } }) => {
      switch (event) {
        case 'signedIn':
          checkUser();
          break;
        case 'signedOut':
          setUser(null);
          break;
      }
    };
    Hub.listen('auth', authListener);
  };

  const handleSignIn = async (username: string, password: string) => {
    const { isSignedIn, nextStep } = await awsSignIn({ username, password });
    if (isSignedIn) {
      const userData = await buildUser();
      if (!userData?.role) {
        await awsSignOut();
        throw new Error('NO_GROUP_ACCESS');
      }
      setUser(userData);
    }
    return { isSignedIn, nextStep };
  };

  const handleSignOut = async () => {
    await awsSignOut();
    setUser(null);
  };

  const handleSignUp = async (username: string, password: string, email: string) => {
    return awsSignUp({
      username,
      password,
      options: { userAttributes: { email } },
    });
  };

  const handleConfirmSignUp = async (username: string, code: string) => {
    return awsConfirmSignUp({ username, confirmationCode: code });
  };

  const value = {
    user,
    loading,
    signIn: handleSignIn,
    signOut: handleSignOut,
    signUp: handleSignUp,
    confirmSignUp: handleConfirmSignUp,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 